// src/pages/RegistrarCalificacion.tsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
  query,
  where,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';

import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';

const schema = yup.object({
  perfil_id: yup.string().required('Debes seleccionar un perfil'),
  perfil_uid: yup.string().required(),
  tipo_perfil: yup.mixed<'asesor' | 'asociado'>().oneOf(['asesor', 'asociado']).required(),
  estrellas: yup.number().min(1, 'Mínimo 1 estrella').max(5, 'Máximo 5 estrellas').required('Debes seleccionar una calificación'),
  razon: yup.string().required('Este campo es requerido'),
  reseña: yup.string().min(20, 'La reseña debe tener al menos 20 caracteres').required('Este campo es requerido'),
  nombre_usuario: yup.string().required('Este campo es requerido'),
  inmobiliaria: yup.string().required('Este campo es requerido'),
  correo_electronico: yup.string().email('Correo electrónico inválido').required('El correo electrónico es requerido')
});

type FormData = yup.InferType<typeof schema>;

interface Perfil {
  id: string;
  uid: string;
  nombre: string;
  tipo: 'asesor' | 'asociado';
  inmobiliaria_nombre?: string;
  foto?: string;
  calificacion?: number;
  anos_experiencia?: number;
}

export default function RegistrarCalificacion() {
  const { user } = useUser();
  const [perfiles, setPerfiles] = useState<Perfil[]>([]);
  const [perfilesFiltrados, setPerfilesFiltrados] = useState<Perfil[]>([]);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState<Perfil | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | 'asesor' | 'asociado'>('todos');
  const [hoverStars, setHoverStars] = useState(0);
  const [loading, setLoading] = useState(false);


  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const estrellasValue = watch('estrellas');
  const formValues = watch(); // Ver todos los valores del formulario

  // 🔍 DEBUG: Ver errores de validación
  console.log('⚠️ Errores de validación:', errors);
  console.log('📝 Valores del formulario:', formValues);

  useEffect(() => {
    fetchPerfiles();
  }, []);

  useEffect(() => {
    filtrarPerfiles();
  }, [searchTerm, filterTipo, perfiles]);

  const fetchPerfiles = async () => {
    try {
      const asesoresSnap = await getDocs(collection(db, 'asesor'));
      const asociadosSnap = await getDocs(collection(db, 'asociado'));

      const asesores = await Promise.all(
        asesoresSnap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          let inmobiliariaNombre = '';

          // Obtener nombre de inmobiliaria
          if (data.inmobiliaria_doc_ids) {
            const inmobiliariaId = Array.isArray(data.inmobiliaria_doc_ids)
              ? data.inmobiliaria_doc_ids[0]
              : data.inmobiliaria_doc_ids;
            if (inmobiliariaId) {
              const inmoSnap = await getDoc(doc(db, 'inmobiliaria', inmobiliariaId));
              if (inmoSnap.exists()) {
                inmobiliariaNombre = inmoSnap.data().nombre || '';
              }
            }
          }

          return {
            id: docSnap.id, // ✅ Usar doc.id de Firestore
            uid: docSnap.id,
            nombre: data.nombre,
            tipo: 'asesor' as const,
            inmobiliaria_nombre: inmobiliariaNombre,
            foto: data.foto || '',
            calificacion: data.calificacion || 0,
            anos_experiencia: data.anos_experiencia || 0
          };
        })
      );

      const asociados = await Promise.all(
        asociadosSnap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          let inmobiliariaNombre = '';

          // Obtener nombre de inmobiliaria
          if (data.inmobiliaria_doc_id) {
            const inmoSnap = await getDoc(doc(db, 'inmobiliaria', data.inmobiliaria_doc_id));
            if (inmoSnap.exists()) {
              inmobiliariaNombre = inmoSnap.data().nombre || '';
            }
          }

          return {
            id: docSnap.id, // ✅ Usar doc.id de Firestore
            uid: docSnap.id,
            nombre: data.nombre,
            tipo: 'asociado' as const,
            inmobiliaria_nombre: inmobiliariaNombre,
            foto: data.foto || '',
            calificacion: data.calificacion || 0,
            anos_experiencia: data.anos_experiencia || 0
          };
        })
      );

      setPerfiles([...asesores, ...asociados]);
    } catch (error) {
      console.error('Error al cargar perfiles:', error);
    }
  };

  const filtrarPerfiles = () => {
    let filtered = perfiles;

    // Filtrar por tipo
    if (filterTipo !== 'todos') {
      filtered = filtered.filter(p => p.tipo === filterTipo);
    }

    // Filtrar por búsqueda (nombre o agencia)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        p => p.nombre.toLowerCase().includes(term) ||
             p.inmobiliaria_nombre?.toLowerCase().includes(term)
      );
    }

    setPerfilesFiltrados(filtered);
  };

  const onPerfilSelect = async (perfil: Perfil) => {
    // Sprint 1 (F3-002): verificación de duplicados por correo aplicada
    // también a usuarios públicos (no solo autenticados). El correo se pedirá
    // en el formulario (schema.correo_electronico requerido).
    // El correo se toma del input actual en el form (si el usuario ya lo escribió)
    // o del perfil autenticado. En ausencia de correo aceptamos mostrar el form,
    // pero la validación final ocurre server-side en el submit.
    const correoParaValidar = (user && user.correo)
      ? user.correo
      : (document.querySelector<HTMLInputElement>('input[name="correo_electronico"]')?.value || '');

    if (correoParaValidar) {
      try {
        const qEmail = query(
          collection(db, 'calificacion'),
          where('perfil_id', '==', perfil.id),
          where('correo_electronico', '==', correoParaValidar.toLowerCase().trim())
        );
        const existingReviewsByEmail = await getDocs(qEmail);

        if (!existingReviewsByEmail.empty) {
          Swal.fire({
            icon: 'warning',
            title: 'Ya existe una reseña',
            text: 'Este correo ya tiene una reseña registrada para este perfil. Solo se permite una reseña por perfil.',
            confirmButtonColor: '#d33'
          });
          return;
        }

        // Validación secundaria solo si el usuario autenticado tiene datos completos
        if (user?.nombre && user?.inmobiliaria_nombre) {
          const qName = query(
            collection(db, 'calificacion'),
            where('perfil_id', '==', perfil.id),
            where('nombre_usuario', '==', user.nombre),
            where('inmobiliaria', '==', user.inmobiliaria_nombre)
          );
          const existingReviewsByName = await getDocs(qName);
          if (!existingReviewsByName.empty) {
            Swal.fire({
              icon: 'warning',
              title: 'Ya has calificado a este perfil',
              text: 'Solo puedes dejar una reseña por perfil.',
              confirmButtonColor: '#d33'
            });
            return;
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error al verificar calificaciones previas:', error);
        }
      }
    }

    setPerfilSeleccionado(perfil);
    setValue('perfil_id', perfil.id);
    setValue('perfil_uid', perfil.uid);
    setValue('tipo_perfil', perfil.tipo);

    console.log('📝 Valores establecidos:', {
      perfil_id: perfil.id,
      perfil_uid: perfil.uid,
      tipo_perfil: perfil.tipo
    });

    // Auto-rellenar nombre, inmobiliaria y correo si el usuario está logueado
    if (user) {
      const nombreUsuario = user.nombre || '';
      const inmobiliaria = user.inmobiliaria_nombre || 'Sin inmobiliaria';
      const correo = user.correo || '';

      setValue('nombre_usuario', nombreUsuario);
      setValue('inmobiliaria', inmobiliaria);
      setValue('correo_electronico', correo);

      console.log('👤 Datos de usuario autorellenados:', {
        nombre_usuario: nombreUsuario,
        inmobiliaria: inmobiliaria,
        correo_electronico: correo
      });
    }
  };

  const calcularCriterios = async (data: FormData) => {
    const perfilRef = doc(db, data.tipo_perfil, data.perfil_uid);
    const perfilSnap = await getDoc(perfilRef);
    const perfil = perfilSnap.data();

    // 1. Años de experiencia
    const años = perfil?.anos_experiencia || 0;
    let expScore = 0;
    if (años >= 10) expScore = 100;
    else if (años >= 6) expScore = 75;
    else if (años >= 3) expScore = 50;
    else if (años > 0) expScore = 25;

    // 2. Cursos
    const qCursos = query(
      collection(db, 'inscripcion'),
      where('perfil_id', '==', data.perfil_id),
      where('completado', '==', true)
    );
    const snapCursos = await getDocs(qCursos);
    const numCursos = snapCursos.size;
    let cursoScore = 0;
    if (numCursos >= 11) cursoScore = 100;
    else if (numCursos >= 7) cursoScore = 75;
    else if (numCursos >= 4) cursoScore = 50;
    else if (numCursos > 0) cursoScore = 25;

    // 3. Participación
    const propiedades = perfil?.propiedad_doc_ids || [];
    const actividad = Array.isArray(propiedades) ? propiedades.length : 0;
    let partScore = actividad >= 10 ? 100 : actividad >= 5 ? 50 : actividad > 0 ? 25 : 0;

    // 4. Perfil Completo
    const completo = perfil?.foto && perfil?.biografia && perfil?.telefono && (perfil?.inmobiliaria_doc_id || perfil?.inmobiliaria_doc_ids);
    const perfilScore = completo ? 100 : 0;

    // 5. Opiniones (solo estrellas de esta reseña).
    // Sprint 11 (F3-008): clamp 0-5 para evitar scores >100 si el cliente envía valor alterado.
    const estrellasClamp = Math.max(0, Math.min(5, Number(data.estrellas) || 0));
    const estrellasScore = (estrellasClamp / 5) * 100;

    // Peso (editable por AMPI)
    const PESOS = {
      experiencia: 0.2,
      cursos: 0.2,
      opiniones: 0.25,
      participacion: 0.2,
      perfil: 0.15
    };

    const calificacionFinal = Math.round(
      expScore * PESOS.experiencia +
      cursoScore * PESOS.cursos +
      estrellasScore * PESOS.opiniones +
      partScore * PESOS.participacion +
      perfilScore * PESOS.perfil
    );

    return { calificacionFinal };
  };

  const onSubmit = async (data: FormData) => {
    console.log('📝 Iniciando envío de reseña...', data);
    setLoading(true);

    try {
      console.log('🔢 Calculando criterios...');
      const { calificacionFinal } = await calcularCriterios(data);
      console.log('✅ Calificación final:', calificacionFinal);

      // Sprint 1 (F3-002): re-validar duplicado por correo justo antes de insertar
      // (protege contra race condition si el usuario tuvo 2 pestañas abiertas).
      const correoNorm = (data.correo_electronico || '').toLowerCase().trim();
      if (correoNorm) {
        const qDup = query(
          collection(db, 'calificacion'),
          where('perfil_id', '==', data.perfil_id),
          where('correo_electronico', '==', correoNorm)
        );
        const dupSnap = await getDocs(qDup);
        if (!dupSnap.empty) {
          setLoading(false);
          Swal.fire({
            icon: 'warning',
            title: 'Ya existe una reseña',
            text: 'Este correo ya tiene una reseña para este perfil.',
          });
          return;
        }
      }

      // Guardar reseña individual (correo normalizado)
      const docRef = await addDoc(collection(db, 'calificacion'), {
        ...data,
        correo_electronico: correoNorm || data.correo_electronico,
        fecha: Timestamp.now(),
        calificacionFinal,
        estatus: 'pendiente',
        origen: user ? 'autenticado' : 'publico',
      });
      console.log('✅ Reseña guardada con ID:', docRef.id);

      // Calcular nuevo promedio total
      const snap = await getDocs(collection(db, 'calificacion'));
      const calificaciones = snap.docs
        .map(doc => doc.data())
        .filter(c => c.perfil_id === data.perfil_id && typeof c.calificacionFinal === 'number' && c.estatus === 'aprobada');

      const promedioTotal = calificaciones.length > 0
        ? Math.round(calificaciones.reduce((acc, curr) => acc + curr.calificacionFinal, 0) / calificaciones.length)
        : calificacionFinal;

      // Actualizar perfil
      const ref = doc(db, data.tipo_perfil, data.perfil_uid);
      await updateDoc(ref, { calificacion: promedioTotal });
      console.log('✅ Perfil actualizado con nueva calificación:', promedioTotal);

      // Mostrar mensaje de éxito
      console.log('🎉 Mostrando mensaje de éxito...');
      await Swal.fire({
        title: '¡Gracias por tu reseña!',
        html: `
          <div class="text-center">
            <div class="mb-3">
              <i class="fa-solid fa-check-circle fa-3x text-success"></i>
            </div>
            <p class="mb-2">Tu reseña ha sido enviada exitosamente.</p>
            <p class="text-muted small">Será revisada por nuestro equipo y publicada pronto.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#28a745',
        allowOutsideClick: false
      });

      console.log('🔄 Limpiando formulario...');
      reset();
      setPerfilSeleccionado(null);
      setSearchTerm('');
      console.log('✅ Proceso completado exitosamente');
    } catch (error: any) {
      console.error('❌ Error al guardar reseña:', error);
      console.error('Detalles del error:', error.message, error.code);

      Swal.fire({
        title: 'Error',
        html: `
          <p>Ocurrió un error al guardar la reseña.</p>
          <p class="text-muted small">${error.message || 'Por favor intenta de nuevo.'}</p>
        `,
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
      console.log('🏁 Finalizando proceso...');
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClass = size === 'sm' ? 'fa-sm' : size === 'lg' ? 'fa-2x' : '';
    return (
      <div className="d-inline-flex align-items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`fa-${star <= rating ? 'solid' : 'regular'} fa-star text-warning me-1 ${sizeClass}`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <>
      <UserNav
        nombreUsuario={user?.nombre}
        nombreInmobiliaria={user?.inmobiliaria_nombre}
      />
      <div className="page-title" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container">
          <h2 className="ipt-title text-white">✍️ Calificar Perfil Profesional</h2>
          <span className="ipn-subtitle text-white">Comparte tu experiencia y ayuda a otros a tomar mejores decisiones</span>
        </div>
      </div>

      <section className="bg-light py-5">
        <div className="container">
          {/* Buscador y Filtros */}
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="form-group">
                <div className="input-group input-group-lg">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="fa-solid fa-search text-primary"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Buscar por nombre o agencia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select form-select-lg"
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value as any)}
              >
                <option value="todos">Todos los perfiles</option>
                <option value="asociado">Asociados</option>
                <option value="asesor">Afiliados</option>
              </select>
            </div>
          </div>

          {/* Lista de Perfiles */}
          {!perfilSeleccionado && (
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fa-solid fa-users me-2"></i>
                      Selecciona el perfil a calificar ({perfilesFiltrados.length} resultados)
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {perfilesFiltrados.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <i className="fa-solid fa-search fa-3x mb-3"></i>
                          <p>No se encontraron perfiles con los filtros aplicados</p>
                        </div>
                      ) : (
                        perfilesFiltrados.map((perfil) => (
                          <div
                            key={perfil.uid}
                            className="list-group-item list-group-item-action d-flex align-items-center p-3"
                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                            onClick={() => onPerfilSelect(perfil)}
                          >
                            <div className="flex-shrink-0 me-3">
                              {perfil.foto ? (
                                <img
                                  src={perfil.foto}
                                  alt={perfil.nombre}
                                  className="rounded-circle"
                                  style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/60?text=' + perfil.nombre.charAt(0);
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                  style={{ width: '60px', height: '60px', fontSize: '24px', fontWeight: 'bold' }}
                                >
                                  {perfil.nombre.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1">{perfil.nombre}</h6>
                              <div className="d-flex align-items-center">
                                {renderStars(Math.round(perfil.calificacion || 0) / 20, 'sm')}
                                <span className="ms-2 text-muted small">
                                  ({perfil.calificacion || 0}/100)
                                </span>
                              </div>
                              <div className="text-muted small">
                                <i className="fa-solid fa-building me-1"></i>
                                {perfil.inmobiliaria_nombre || 'Sin agencia'}
                                <span className="mx-2">•</span>
                                <span className="badge bg-secondary">
                                  {perfil.tipo === 'asociado' ? 'Asociado' : 'Afiliado'}
                                </span>
                                {(perfil.anos_experiencia && perfil.anos_experiencia > 0) && (
                                  <>
                                    <span className="mx-2">•</span>
                                    <i className="fa-solid fa-briefcase me-1"></i>
                                    {perfil.anos_experiencia} años
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <i className="fa-solid fa-chevron-right text-primary"></i>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Formulario de Calificación */}
          {perfilSeleccionado && (
            <div className="row">
              <div className="col-md-8 offset-md-2">
                {/* Card del Perfil Seleccionado */}
                <div className="card shadow-lg border-0 mb-4" style={{ borderRadius: '15px' }}>
                  <div className="card-body text-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-0 end-0 m-3"
                      onClick={() => setPerfilSeleccionado(null)}
                    >
                      <i className="fa-solid fa-times"></i> Cambiar
                    </button>
                    {perfilSeleccionado.foto ? (
                      <img
                        src={perfilSeleccionado.foto}
                        alt={perfilSeleccionado.nombre}
                        className="rounded-circle border border-4 border-white"
                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/120?text=' + perfilSeleccionado.nombre.charAt(0);
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-white text-primary d-inline-flex align-items-center justify-content-center"
                        style={{ width: '120px', height: '120px', fontSize: '48px', fontWeight: 'bold' }}
                      >
                        {perfilSeleccionado.nombre.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <h3 className="text-white mt-3 mb-2">{perfilSeleccionado.nombre}</h3>
                    <div className="mb-2">
                      {renderStars(Math.round(perfilSeleccionado.calificacion || 0) / 20, 'md')}
                    </div>
                    <p className="text-white mb-1">
                      <i className="fa-solid fa-building me-2"></i>
                      {perfilSeleccionado.inmobiliaria_nombre || 'Sin agencia'}
                    </p>
                    <span className="badge bg-white text-primary px-3 py-2">
                      {perfilSeleccionado.tipo === 'asociado' ? 'Asociado' : 'Afiliado'}
                    </span>
                  </div>
                </div>

                {/* Card del Usuario que Califica */}
                {user && (
                  <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '15px', background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)' }}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          {user.foto ? (
                            <img
                              src={user.foto}
                              alt={user.nombre}
                              className="rounded-circle"
                              style={{ width: '60px', height: '60px', objectFit: 'cover', border: '3px solid white' }}
                              onError={(e) => {
                                e.currentTarget.src = 'https://via.placeholder.com/60?text=' + (user.nombre?.charAt(0) || 'U');
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                              style={{ width: '60px', height: '60px', fontSize: '24px', fontWeight: 'bold', border: '3px solid white' }}
                            >
                              {user.nombre?.charAt(0).toUpperCase() || 'U'}
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <p className="mb-0 text-muted small">
                            <i className="fa-solid fa-user-edit me-1"></i>
                            Publicando como:
                          </p>
                          <h6 className="mb-0 fw-bold">{user.nombre || 'Usuario'}</h6>
                          <small className="text-muted">
                            <i className="fa-solid fa-building me-1"></i>
                            {user.inmobiliaria_nombre || 'Sin inmobiliaria'}
                          </small>
                        </div>
                        <div>
                          <span className="badge bg-primary">
                            {user.tipo === 'asociado' ? 'Asociado' : user.tipo === 'asesor' ? 'Afiliado' : 'Usuario'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Formulario */}
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="card shadow border-0" style={{ borderRadius: '15px' }}>
                    <div className="card-body p-4">
                      <h4 className="mb-4">
                        <i className="fa-solid fa-star text-warning me-2"></i>
                        Tu Opinión
                      </h4>

                      {/* Calificación con Estrellas Interactivas */}
                      <div className="mb-4 text-center p-4" style={{ background: '#f8f9fa', borderRadius: '10px' }}>
                        <label className="form-label d-block mb-3">
                          <strong>¿Cómo calificas tu experiencia?</strong>
                        </label>
                        <div className="d-flex justify-content-center align-items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i
                              key={star}
                              className={`fa-${star <= (hoverStars || estrellasValue || 0) ? 'solid' : 'regular'} fa-star fa-3x mx-2`}
                              style={{
                                color: star <= (hoverStars || estrellasValue || 0) ? '#ffc107' : '#dee2e6',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={() => setHoverStars(star)}
                              onMouseLeave={() => setHoverStars(0)}
                              onClick={() => setValue('estrellas', star)}
                            ></i>
                          ))}
                        </div>
                        {estrellasValue && (
                          <p className="mt-3 mb-0 text-primary fw-bold">
                            {estrellasValue === 5 && '¡Excelente! ⭐⭐⭐⭐⭐'}
                            {estrellasValue === 4 && 'Muy Bueno ⭐⭐⭐⭐'}
                            {estrellasValue === 3 && 'Bueno ⭐⭐⭐'}
                            {estrellasValue === 2 && 'Regular ⭐⭐'}
                            {estrellasValue === 1 && 'Necesita Mejorar ⭐'}
                          </p>
                        )}
                        {errors.estrellas && (
                          <small className="text-danger d-block mt-2">{errors.estrellas.message}</small>
                        )}
                      </div>

                      <input type="hidden" {...register('perfil_id')} />
                      <input type="hidden" {...register('perfil_uid')} />
                      <input type="hidden" {...register('tipo_perfil')} />
                      <input type="hidden" {...register('estrellas')} />

                      {/* Razón */}
                      <div className="mb-4">
                        <label className="form-label">
                          <i className="fa-solid fa-tag me-2"></i>
                          <strong>Título de tu reseña</strong>
                        </label>
                        <input
                          {...register('razon')}
                          className="form-control form-control-lg"
                          placeholder="Ej: Excelente profesional, muy recomendable"
                          style={{ borderRadius: '10px' }}
                        />
                        {errors.razon && (
                          <small className="text-danger">{errors.razon.message}</small>
                        )}
                      </div>

                      {/* Reseña */}
                      <div className="mb-4">
                        <label className="form-label">
                          <i className="fa-solid fa-comment-dots me-2"></i>
                          <strong>Cuéntanos tu experiencia</strong>
                        </label>
                        <textarea
                          {...register('reseña')}
                          className="form-control"
                          rows={6}
                          placeholder="Describe tu experiencia trabajando con este profesional. ¿Qué destacarías? ¿Qué te ayudó más?"
                          style={{ borderRadius: '10px' }}
                        />
                        {errors.reseña && (
                          <small className="text-danger">{errors.reseña.message}</small>
                        )}
                      </div>

                      <hr className="my-4" />

                      <h5 className="mb-3">Tus Datos</h5>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            <i className="fa-solid fa-user me-2"></i>
                            <strong>Tu Nombre *</strong>
                          </label>
                          <input
                            {...register('nombre_usuario')}
                            className="form-control"
                            placeholder="Nombre completo"
                            style={{ borderRadius: '10px' }}
                          />
                          {errors.nombre_usuario && (
                            <small className="text-danger">{errors.nombre_usuario.message}</small>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            <i className="fa-solid fa-building me-2"></i>
                            <strong>Tu Inmobiliaria *</strong>
                          </label>
                          <input
                            {...register('inmobiliaria')}
                            className="form-control"
                            placeholder="Nombre de tu inmobiliaria"
                            style={{ borderRadius: '10px' }}
                          />
                          {errors.inmobiliaria && (
                            <small className="text-danger">{errors.inmobiliaria.message}</small>
                          )}
                        </div>

                        <div className="col-md-12 mb-3">
                          <label className="form-label">
                            <i className="fa-solid fa-envelope me-2"></i>
                            <strong>Tu Correo Electrónico *</strong>
                          </label>
                          <input
                            {...register('correo_electronico')}
                            type="email"
                            className="form-control"
                            placeholder="tu@correo.com"
                            style={{ borderRadius: '10px' }}
                          />
                          {errors.correo_electronico && (
                            <small className="text-danger d-block">{errors.correo_electronico.message}</small>
                          )}
                          <small className="text-muted d-block mt-2">
                            <i className="fa-solid fa-shield-alt me-1"></i>
                            Tu correo se usa solo para evitar calificaciones duplicadas y no será visible públicamente
                          </small>
                        </div>
                      </div>

                      <div className="d-grid gap-2 mt-4">
                        <button
                          className="btn btn-lg text-white"
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '10px',
                            padding: '15px'
                          }}
                          type="submit"
                          disabled={loading}
                          onClick={() => {
                            console.log('🖱️ Click en botón Publicar Reseña');
                            console.log('📋 Errores actuales:', errors);
                          }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-paper-plane me-2"></i>
                              Publicar Reseña
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-lg"
                          style={{ borderRadius: '10px' }}
                          onClick={() => setPerfilSeleccionado(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .form-control::placeholder {
          color: #6c757d !important;
          opacity: 0.7;
        }

        .form-select::placeholder {
          color: #6c757d !important;
          opacity: 0.7;
        }

        .list-group-item:hover {
          background-color: #f8f9fa;
          transform: translateX(5px);
        }
      `}</style>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
